import {
	Box,
	Text,
	Button,
	Stack,
	Icons,
	Table,
	Tooltip,
	Container,
	Inline,
	Heading,
	Divider,
	Dropdown,
	Badge,
	Col,
	Component,
	Grid,
	joinAttributes,
	Spinner,
	Stars,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Time,
	Money,
	Alert,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { Breadcrumbs, Link } from "@sembark-travel/ui-router"
import {
	dateToQuery,
	dateToUTCString,
	parseDateFromQuery,
	startOf,
	formatDate,
	localOrUtcTimeToLocalDate,
} from "@sembark-travel/datetime-utils"
import { isNumeric, withOrdinalSuffix } from "@sembark-travel/number-utils"
import {
	NavLink,
	ButtonLink,
	useLocationQuery,
	queryToSearch,
} from "@sembark-travel/ui-router"
import {
	Search,
	ListView,
	useSearch,
	TSearchParams,
	areAdvancedFiltersAppliedDefault,
} from "@sembark-travel/ui-list"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import React, { useEffect, useMemo } from "react"
import { AccountsList } from "../Accounting"
import config from "../config"
import { ContactsList, PhoneNumber, Email, IContact } from "../Contacts"
import { SelectHotelBookingStages } from "../HotelBookingStages"
import { PaymentsList } from "../Payments"
import { generatePath } from "../router-utils"
import { getDisplayNameForStage, IBooking } from "../HotelBookings"
import { ITrip } from "../Trips"
import { UpdateHotel } from "./Edit"
import {
	formatClosedDates,
	IHotel,
	useHotel,
	TDateInterval,
	TSoldoutRoomInterval,
	TBlackoutRoomInterval,
} from "./store"
import { useCheckPermissions, PERMISSIONS } from "../Auth"
import { ImageMediaItem } from "./../Media"
import {
	SelectField,
	DatePickerField,
	SwitchInputField,
	Form,
	withServerErrors,
	isTruthy,
	arrayMutators,
	validateFormValues,
} from "@sembark-travel/ui-form"
import { CreateHotelNotes, HotelNotesList } from "../HotelNotes"
import { ISupplier } from "../Supplier"
import { TemporaryStorage } from "../TemporaryStorage"
import { HotelBlackoutDateIntervalsField } from "./NewItem"
import { moneyParseByDecimal } from "@sembark-travel/money"
import { EntryTypeIcon } from "../shared/EntryTypeConfig"
import * as Validator from "yup"

function XHR(xhr: XHRInstance) {
	return {
		async createContact(
			id: string | number,
			contactData: Record<string, unknown>
		): Promise<IContact> {
			return xhr
				.post(`/hotel-contacts`, {
					hotel_id: id,
					...contactData,
				})
				.then((resp) => resp.data.data)
		},
		async getBookings(
			params: unknown
		): Promise<IListResponse<IBooking & { trip: ITrip }>> {
			return xhr.get("/hotel-bookings", { params }).then((resp) => resp.data)
		},
		async destroyNote(id: number | string) {
			return xhr
				.delete<{ message: string }>(`/hotel-notes/${id}`)
				.then((res) => res.data)
		},
	}
}

export function HotelDetails({
	hotelId,
	children,
}: {
	hotelId: string
	children?: (props: { hotel: IHotel; onChange: () => void }) => React.ReactNode
}) {
	const { hotel, isFetching, fetchHotel: getHotel } = useHotel(hotelId)
	const { hasPermission, hasAnyPermission } = useCheckPermissions()
	const xhr = useXHR()
	if (isFetching && !hotel) return <Spinner alignCenter padding="4" />
	if (!hotel) return <Alert status="error">Hotel does not exist.</Alert>
	const {
		name,
		group,
		stars,
		location,
		address,
		latest_media,
		deleted_at,
		url,
	} = hotel
	return (
		<Box>
			<Breadcrumbs
				title="Hotel Details"
				items={[
					[generatePath("/hotels"), "Hotels"],
					["", hotel.name],
				]}
				actions={
					hasPermission(PERMISSIONS.CREATE_HOTELS) ? (
						<Inline gap="4">
							<Component initialState={false}>
								{({ state, setState }) => (
									<>
										<Button onClick={() => setState(true)} size="sm">
											<Icons.Pencil /> Edit
										</Button>
										<Dialog open={state} onClose={() => setState(false)} xl>
											<Dialog.Header>
												<Dialog.Title>Edit Hotel Details</Dialog.Title>
											</Dialog.Header>
											<Dialog.Body>
												<UpdateHotel
													hotel={hotel}
													onSuccess={() => {
														getHotel()
														setState(false)
													}}
													onCancel={() => setState(false)}
												/>
											</Dialog.Body>
										</Dialog>
									</>
								)}
							</Component>
							<Component<"archive" | "restore" | undefined>
								initialState={undefined}
							>
								{({ state, setState }) => (
									<>
										<Dropdown alignRight>
											<Dropdown.ToggleButton size="sm" level="tertiary">
												<Icons.DotsVertical />
											</Dropdown.ToggleButton>
											<Dropdown.Menu>
												{!deleted_at ? (
													<Dropdown.MenuItem
														onClick={() => setState("archive")}
													>
														<Icons.Ban /> Disable
													</Dropdown.MenuItem>
												) : (
													<Dropdown.MenuItem
														onClick={() => setState("restore")}
													>
														<Icons.Refresh /> Restore
													</Dropdown.MenuItem>
												)}
											</Dropdown.Menu>
										</Dropdown>
										<Dialog
											open={state === "archive"}
											onClose={() => setState(undefined)}
											sm
										>
											<Dialog.Header>
												<Dialog.Title>Disable Hotel</Dialog.Title>
											</Dialog.Header>
											<Form<{ hotel_id: number }>
												initialValues={{ hotel_id: hotel.id }}
												onSubmit={withServerErrors(async (values) => {
													const resp = await xhr.delete<{ message: string }>(
														`/hotels/${values.hotel_id}`
													)
													getHotel()
													showSnackbar(resp.data.message)
													setState(undefined)
												})}
												subscription={{ submitting: true }}
											>
												{({ submitting, handleSubmit }) => (
													<form noValidate onSubmit={handleSubmit}>
														<Dialog.Body>
															<Text>
																This hotel will be removed from all hotels
																listing and selection pages. Existing usage of
																this hotel will not be affected.
															</Text>
															<Divider sm />
															<Text color="warning">
																Are you sure you want to disable this hotel ?
															</Text>
														</Dialog.Body>
														<Dialog.Footer>
															<Button
																type="submit"
																disabled={submitting}
																status="warning"
															>
																{submitting
																	? "Disabling..."
																	: "I understand, disabled it."}
															</Button>
															<Button
																onClick={() => setState(undefined)}
																disabled={submitting}
															>
																Cancel
															</Button>
														</Dialog.Footer>
													</form>
												)}
											</Form>
										</Dialog>
										<Dialog
											open={state === "restore"}
											onClose={() => setState(undefined)}
											sm
										>
											<Dialog.Header>
												<Dialog.Title>Restore disabled Hotel</Dialog.Title>
											</Dialog.Header>
											<Form<{ hotel_id: number }>
												initialValues={{ hotel_id: hotel.id }}
												onSubmit={withServerErrors(async (values) => {
													const resp = await xhr.patch<{ message: string }>(
														`/hotels/${values.hotel_id}/restore`
													)
													getHotel()
													showSnackbar(resp.data.message)
													setState(undefined)
												})}
												subscription={{ submitting: true }}
											>
												{({ submitting, handleSubmit }) => (
													<form noValidate onSubmit={handleSubmit}>
														<Dialog.Body>
															<Text>
																Are you sure you want to restore this disabled
																hotel ?
															</Text>
														</Dialog.Body>
														<Dialog.Footer>
															<Button type="submit" disabled={submitting}>
																{submitting ? "Restoring..." : "Restore"}
															</Button>
															<Button
																onClick={() => setState(undefined)}
																disabled={submitting}
															>
																Cancel
															</Button>
														</Dialog.Footer>
													</form>
												)}
											</Form>
										</Dialog>
									</>
								)}
							</Component>
						</Inline>
					) : null
				}
			/>
			<Container paddingY="8" bgColor="default">
				<Inline gap="6" collapseBelow="md" justifyContent="between">
					<Stack gap="4">
						<Stack gap="1">
							<Heading as="h3">
								<Inline gap="2" alignItems="center">
									{name}
									<Box>
										<EntryTypeIcon
											entryType={hotel?.entry_type}
											createdBy={hotel?.created_by?.name}
										/>
									</Box>
								</Inline>
							</Heading>
							<table>
								<tbody>
									<tr>
										<Box as="td" color="muted" paddingRight="2">
											Location:
										</Box>
										<td>{location.name}</td>
									</tr>
									{stars ? (
										<tr>
											<Box as="td" color="muted" paddingRight="2">
												Category:
											</Box>
											<td>
												<Stars stars={stars} />
											</td>
										</tr>
									) : null}
									{group ? (
										<tr>
											<Box as="td" color="muted" paddingRight="2">
												Hotel Group:
											</Box>
											<td>
												<Link
													color="accent"
													to={generatePath("/hotel-groups/:groupId", {
														groupId: group.id.toString(),
													})}
												>
													{group.name}
												</Link>
											</td>
										</tr>
									) : null}
								</tbody>
							</table>
						</Stack>
						{url ? (
							<Stack>
								<Text fontSize="sm" color="muted" fontWeight="semibold">
									Photos / Details URL
								</Text>
								<Box>
									<Box
										as="a"
										href={url}
										target="_blank"
										rel="noopener noreferrer"
										color="accent"
										maxWidth="sm"
										textOverflow="truncate"
										display="block"
									>
										{url.replace(/https?:\/\/(www\.)?/, "")}
									</Box>
								</Box>
							</Stack>
						) : null}
						{deleted_at ? (
							<Box>
								<Badge warning>Disabled</Badge>
							</Box>
						) : null}
					</Stack>
					<Stack gap="1">
						<Text fontWeight="semibold">Address</Text>
						<Address address={address} />
					</Stack>
					<Stack style={{ maxWidth: "300px" }}>
						<ImageMediaItem
							media={latest_media}
							help="Image should be 750x500 JPEG/PNG file with less than 1 MB in size. For non-branded PDFs, avoid logos or branding."
							crop={{
								aspect: 1.5 / 1,
								minHeight: 100,
								minWidth: 150,
							}}
							canRemove
							canEdit={hasPermission(PERMISSIONS.CREATE_HOTELS)}
							onSubmit={async (data) => {
								await xhr.patch(`/hotels/${hotel.id}/image`, data)
								await getHotel()
							}}
						/>
					</Stack>
				</Inline>
			</Container>
			<Box borderTopWidth="1">
				<Tabs layout="col">
					<TabsList>
						<NavLink
							to={generatePath("/hotels/:hotelId", {
								hotelId: String(hotel.id),
							})}
							end
						>
							Details
						</NavLink>
						<NavLink
							to={generatePath("/hotels/:hotelId/notes", {
								hotelId: String(hotel.id),
							})}
							end
						>
							Hotel Notes
						</NavLink>
						{hasAnyPermission(
							PERMISSIONS.VIEW_HOTEL_BOOKINGS,
							PERMISSIONS.MANAGE_ACCOUNTING
						) ? (
							<NavLink
								to={generatePath("/hotels/:hotelId/bookings", {
									hotelId: String(hotel.id),
								})}
							>
								Bookings
							</NavLink>
						) : null}
						{hasAnyPermission(
							PERMISSIONS.VIEW_HOTEL_BOOKINGS,
							PERMISSIONS.MANAGE_ACCOUNTING
						) ? (
							<NavLink
								to={generatePath("/hotels/:hotelId/payments/outgoing", {
									hotelId: String(hotel.id),
								})}
							>
								Payments
							</NavLink>
						) : null}
						{hasPermission(PERMISSIONS.MANAGE_ACCOUNTING) ? (
							<NavLink
								to={generatePath("/hotels/:hotelId/accounting", {
									hotelId: String(hotel.id),
								})}
							>
								Accounting
							</NavLink>
						) : null}
						<NavLink
							to={generatePath("/hotels/:hotelId/hotel-activities", {
								hotelId: String(hotel.id),
							})}
						>
							Activities
						</NavLink>
					</TabsList>
					<TabContent>
						{children
							? children({
									hotel,
									onChange: getHotel,
								})
							: null}
					</TabContent>
				</Tabs>
			</Box>
		</Box>
	)
}

export function Address({ address }: { address: IHotel["address"] }) {
	const location = address.location
	const city_state_names = [location.city, location.state]
		.map((d) => d?.name)
		.filter((x): x is string => Boolean(x))
	const city_state_line = city_state_names.join(", ")
	let line_1 = address.line_1?.replace(city_state_line, "").trim() || ""
	let line_2 = address.line_2?.replace(city_state_line, "").trim() || ""
	let landmark = address.landmark?.replace(city_state_line, "").trim() || ""
	if (city_state_names.indexOf(line_1) !== -1) {
		line_1 = ""
	}
	if (city_state_names.indexOf(line_2) !== -1) {
		line_2 = ""
	}
	if (city_state_names.indexOf(landmark) !== -1) {
		landmark = ""
	}
	return (
		<Stack as="address">
			{line_1 || line_2 || landmark ? (
				<Text>{[line_1, line_2, landmark].filter(Boolean).join(", ")}</Text>
			) : null}
			{city_state_line ? <Text>{city_state_line}</Text> : location.name}
			<Text>
				{[address.location?.country?.name, address.postcode]
					.filter(Boolean)
					.join(", ")}
			</Text>
			{address.phone_numbers?.length ? (
				<Box>
					<PhoneNumber value={address.phone_numbers} />
				</Box>
			) : null}
			{address.email ? (
				<Box>
					<Email value={address.email} />
				</Box>
			) : null}
		</Stack>
	)
}

export function HotelBasicDetails({
	hotel,
	onChange,
}: {
	hotel: IHotel
	onChange: () => void
}) {
	const {
		extra_bed_child_age_start,
		extra_bed_child_age_end,
		meal_plans,
		room_types,
		payment_preference,
		trip_destinations,
		checkin_at_local,
		checkin_at,
		checkout_at_local,
		checkout_at,
		created_at,
		created_by,
		updated_at,
		updated_by,
		deleted_at,
		deleted_by,
		id,
	} = hotel
	const { hasPermission } = useCheckPermissions()
	const hasBlackoutDates = room_types.some(
		(roomType) => roomType.black_out_date_intervals.length > 0
	)
	const xhr = useXHR()
	const { soldOutRoomTypes, blackoutRoomTypes } = useMemo(() => {
		const soldOutRoomTypes = room_types
			.map((roomType) => ({
				...roomType,
				black_out_date_intervals:
					roomType.black_out_date_intervals?.filter((interval) =>
						isTruthy(interval.stop_sales)
					) || [],
			}))
			.filter((roomType) => roomType.black_out_date_intervals.length > 0)

		const blackoutRoomTypes = room_types
			.map((roomType) => ({
				...roomType,
				black_out_date_intervals:
					roomType.black_out_date_intervals?.filter(
						(interval) => !isTruthy(interval.stop_sales)
					) || [],
			}))
			.filter((roomType) => roomType.black_out_date_intervals.length > 0)

		return {
			soldOutRoomTypes,
			blackoutRoomTypes,
		}
	}, [room_types])

	return (
		<Container paddingY="8" bgColor="default">
			<Grid gap="4">
				<Col>
					<Grid gap="4">
						<Col>
							<Box
								fontSize="sm"
								marginBottom="1"
								fontWeight="semibold"
								color="muted"
							>
								Available Meal Plans
							</Box>
							<Box>
								{meal_plans.map((mealPlan) => mealPlan.name).join(" • ")}
							</Box>
						</Col>
						<Col>
							<Inline gap="4">
								<Stack gap="1">
									<Box fontSize="sm" fontWeight="semibold" color="muted">
										Check-In
									</Box>
									<Box>
										{formatDate(
											localOrUtcTimeToLocalDate(checkin_at_local, checkin_at),
											"HH:mm"
										)}{" "}
										hrs
									</Box>
								</Stack>
								<Stack gap="1">
									<Box fontSize="sm" fontWeight="semibold" color="muted">
										Check-Out
									</Box>
									<Box>
										{formatDate(
											localOrUtcTimeToLocalDate(checkout_at_local, checkout_at),
											"HH:mm"
										)}{" "}
										hrs
									</Box>
								</Stack>
							</Inline>
						</Col>
						<Col xs={12} sm>
							<Box
								fontSize="sm"
								marginBottom="1"
								fontWeight="semibold"
								color="muted"
							>
								Extra bed child ages
							</Box>
							<Box>
								From {extra_bed_child_age_start} to {extra_bed_child_age_end}{" "}
								years
							</Box>
						</Col>
						<Col xs={12}>
							<Box
								fontSize="sm"
								marginBottom="1"
								fontWeight="semibold"
								color="muted"
							>
								Available Room Types
							</Box>
							<Box>
								{joinAttributes(
									...room_types.map((roomType) => (
										<Box as="span">
											{roomType.name}
											<Box fontSize="xs" as="span">
												{" ("}
												{joinAttributes(
													<Box
														as="span"
														title={`${pluralize(
															"extra bed",
															roomType.allowed_extra_beds,
															true
														)} allowed`}
													>
														{roomType.allowed_extra_beds} EBs
													</Box>,
													<Box
														as="span"
														title={`${pluralize(
															"Adult extra bed",
															roomType.allowed_adults_with_extra_bed,
															true
														)} allowed`}
													>
														{roomType.allowed_adults_with_extra_bed} AWEBs
													</Box>,
													<Box
														as="span"
														title={`${pluralize(
															"Child extra bed",
															roomType.allowed_children_with_extra_bed,
															true
														)} allowed`}
													>
														{roomType.allowed_children_with_extra_bed} CWEBs
													</Box>,
													isNumeric(
														roomType.allowed_children_without_extra_bed
													) ? (
														<Box
															as="span"
															title={`${pluralize(
																"Child no-extra bed",
																roomType.allowed_children_without_extra_bed,
																true
															)} allowed`}
														>
															{roomType.allowed_children_without_extra_bed} CNBs
														</Box>
													) : null,
													roomType.no_of_rooms ? (
														<Box as="span">
															{pluralize("Room", roomType.no_of_rooms, true)}
														</Box>
													) : null
												)}
												)
											</Box>
										</Box>
									))
								)}
							</Box>
						</Col>
						<Col xs={12}>
							{/* Stop Sale Room Types */}
							<RoomTypeDateIntervalRestrictions
								roomTypes={soldOutRoomTypes}
								title="Soldout Room Types"
								emptyMessage="No soldout date set for any type of room"
								tooltipPrefix="Unavailable from"
							/>
							{/* Blackout Room Types */}
							<RoomTypeDateIntervalRestrictions
								roomTypes={blackoutRoomTypes}
								title="Blackout Room Types"
								emptyMessage="No blackout date set for any type of room"
								tooltipPrefix="On High Demand from"
							/>
							{hasPermission(PERMISSIONS.CREATE_HOTELS) ? (
								<EditClosingIntervals
									room_types={room_types}
									same_black_out_date_intervals_for_all_room_types={
										hotel.same_black_out_date_intervals_for_all_room_types ??
										true
									}
									same_sold_out_date_intervals_for_all_room_types={
										hotel.same_sold_out_date_intervals_for_all_room_types ??
										true
									}
									onSubmit={async (data) => {
										await xhr.patch(
											`/hotels/${id}/rooms-types/black-out-date-intervals`,
											data
										)
										showSnackbar("Closed dates updated successfully")
										onChange()
									}}
								>
									{({ onEdit }) => (
										<Button onClick={onEdit} size="sm" level="primary">
											{hasBlackoutDates ? "Manage" : "Add"} Intervals
										</Button>
									)}
								</EditClosingIntervals>
							) : null}
						</Col>
					</Grid>
				</Col>
				<Col xs={12} md={4} lg={3}>
					<Stack gap="4">
						<Stack gap="1">
							<Box fontSize="sm" fontWeight="semibold" color="muted">
								Payment Preference
							</Box>
							<Box>
								{payment_preference ? (
									payment_preference.name
								) : (
									<Box color="muted">Not Set</Box>
								)}
							</Box>
						</Stack>
						<Stack gap="1">
							<Box fontSize="sm" fontWeight="semibold" color="muted">
								Trip Destinations
							</Box>
							<Box>
								{trip_destinations?.length ? (
									trip_destinations.map((t) => t.name).join(", ")
								) : (
									<Box color="muted">All</Box>
								)}
							</Box>
						</Stack>
					</Stack>
				</Col>
			</Grid>
			<Divider sm />
			{joinAttributes(
				<Text as="span">
					Created on <Time timestamp={created_at} />
					{created_by ? ` by ${created_by.name}` : null}
				</Text>,
				deleted_at ? (
					<Text as="span" color="danger">
						Disabled on <Time timestamp={deleted_at} />
						{deleted_by ? ` by ${deleted_by.name}` : null}
					</Text>
				) : null,
				updated_at && updated_by ? (
					<Text as="span">
						Updated on <Time timestamp={updated_at} /> by {updated_by.name}
					</Text>
				) : null
			)}
			<Divider sm />
			<Inline gap="8" collapseBelow="md">
				{hasPermission(PERMISSIONS.MANAGE_ACCOUNTING) ? (
					<Stack width={{ sm: "full", md: "1/2" }} gap="4">
						<Heading as="h4" fontSize="md">
							<Icons.BankNotes color="muted" opacity="50" size="6" /> Accounting
						</Heading>
						{hotel.accounting_accounts?.length ? (
							<Box>
								{hotel.accounting_accounts.map((a) => (
									<Box key={a.id}>
										<Box
											fontWeight="semibold"
											textTransform="uppercase"
											letterSpacing="wider"
											color="muted"
											marginBottom="1"
											fontSize="sm"
										>
											{a.name}
										</Box>
										{a.assets?.length ? (
											<Box>
												{a.assets.map((asset) => (
													<Box key={asset.type}>
														<Box
															as="span"
															fontSize="xl"
															fontWeight="semibold"
															color={asset.amount > 0 ? "success" : "danger"}
														>
															{asset.amount}
														</Box>
														<Box as="span" fontSize="sm" marginLeft="1">
															{asset.type}
														</Box>
													</Box>
												))}
											</Box>
										) : (
											<Box>
												<Box color="muted">n/a</Box>
											</Box>
										)}
									</Box>
								))}
							</Box>
						) : (
							<Stack gap="2">
								<Text fontSize="md">
									No accounting activities for this hotel
								</Text>
								<Text fontSize="sm" color="muted">
									Log payments regarding bookings or services for this hotel to
									manage the accounting.
								</Text>
								<Box>
									<ButtonLink
										to={generatePath("/hotels/:hotelId/payments/outgoing", {
											hotelId: String(hotel.id),
										})}
										size="sm"
									>
										View Due Payments <Icons.ChevronDown rotate="270" />
									</ButtonLink>
								</Box>
							</Stack>
						)}
					</Stack>
				) : null}
				<Box flex="1">
					<ContactsList
						contacts={hotel.contacts}
						phoneNumberRequired
						onCreate={async (data) => {
							const resp = await XHR(xhr).createContact(hotel.id, data)
							onChange()
							return resp
						}}
						onChange={() => onChange()}
						readOnly={!hasPermission(PERMISSIONS.CREATE_HOTELS)}
					/>
				</Box>
			</Inline>
		</Container>
	)
}

type TFilters = TSearchParams & {
	supplier?: string
	has_due_payments?: boolean
	booking_stages?: Array<{ id: string; name: string }>
	stay_after?: Date
	stay_before?: Date
}

type TFiltersInQuery = TSearchParams & {
	supplier?: string
	hdp?: 1
	bs?: Array<string>
	stay_after?: string
	stay_before?: string
}

function filtersToLocationQuery(filters: TFilters): TFiltersInQuery {
	const {
		supplier,
		q,
		page,
		has_due_payments,
		booking_stages,
		stay_after,
		stay_before,
	} = filters
	const query: TFiltersInQuery = { q, page }
	if (supplier) {
		query.supplier = supplier
	}
	if (q) query.q = q
	if (page) query.page = page
	if (has_due_payments) query.hdp = 1
	if (booking_stages?.length) {
		query.bs = booking_stages.map((b) => `${b.id}#${b.name}`)
	}
	if (stay_after) {
		query.stay_after = dateToQuery(stay_after)
	}
	if (stay_before) {
		query.stay_before = dateToQuery(stay_before)
	}
	return query
}

function locationQueryToFilters(query: TFiltersInQuery): TFilters {
	const { supplier, q, page, hdp, bs, stay_after, stay_before } = query
	const filters: TFilters = { q }
	if (supplier) {
		filters.supplier = supplier
	}
	if (page) filters.page = page
	if (hdp) filters.has_due_payments = true
	if (bs?.length) {
		filters.booking_stages = bs.map((b) => {
			const [id, ...name] = b.split("#")
			return {
				id,
				name: name.join("#"),
			}
		})
	}
	if (stay_after) {
		filters.stay_after = parseDateFromQuery(stay_after)
	}
	if (stay_before) {
		filters.stay_before = parseDateFromQuery(stay_before)
	}
	return filters
}

function filtersToRequestParams(filters: TFilters) {
	const {
		supplier,
		q,
		page,
		has_due_payments,
		booking_stages,
		stay_after,
		stay_before,
	} = filters
	const isDirectBooking = supplier === "0" || false
	return {
		page: page || 1,
		q,
		supplier: !isDirectBooking
			? supplier === "all"
				? undefined
				: supplier
			: undefined,
		only_direct_hotel_bookings: isDirectBooking ? 1 : undefined,
		has_due_payments: has_due_payments ? 1 : null,
		booking_stages: booking_stages?.map((b) => b.id),
		stay_after: stay_after ? dateToUTCString(startOf(stay_after, "day")) : null,
		stay_before: stay_before
			? dateToUTCString(startOf(stay_before, "day"))
			: null,
	}
}

export function HotelBookings({
	params: propParams,
}: {
	params?: {
		hotels?: Array<number>
		hotel_groups?: Array<number>
		booking_suppliers?: ISupplier[]
		direct_bookings_exists?: boolean
	}
}) {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToLocationQuery,
		fromQuery: locationQueryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
		supplier:
			query.supplier || propParams?.direct_bookings_exists ? "0" : "all",
	})
	const showProperty = propParams?.hotels?.length !== 1
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Box paddingTop="8">
			<Search
				placeholder="Search by Guest, Services etc..."
				Filters={Filters}
				resetParams={(params) => ({
					q: "",
					supplier:
						params.supplier || propParams?.direct_bookings_exists ? "0" : "all",
					page: 1,
				})}
				initialParams={params}
				onSearch={(newParams) => {
					setParams({ ...newParams, page: 1 })
				}}
				title="Bookings"
				areAdvancedFiltersApplied={(params) => {
					const { supplier, has_due_payments, ...otherParams } = params
					return (
						has_due_payments || areAdvancedFiltersAppliedDefault(otherParams)
					)
				}}
				actions={
					<>
						<TemporaryStorage
							initUrl="/hotel-bookings/export"
							initParams={{
								...filtersToRequestParams(params),
								hotels: propParams?.hotels,
								hotel_groups: propParams?.hotel_groups,
								timezone_offset: config.timezoneOffset,
							}}
						>
							{({ message, generate, url, reset, isGenerating }) =>
								url ? (
									<Button
										as="a"
										href={url}
										target="_blank"
										download
										level="secondary"
										onClick={() => reset()}
									>
										{message}
									</Button>
								) : (
									<Button
										onClick={generate}
										disabled={isGenerating}
										level="secondary"
									>
										{message || (
											<>
												<Icons.DocumentDownload />
											</>
										)}{" "}
										Excel
									</Button>
								)
							}
						</TemporaryStorage>
					</>
				}
			>
				{({ searchParams, setSearchParams }) => (
					<Tabs>
						<TabsList>
							{propParams?.direct_bookings_exists ? (
								<TabItem
									active={searchParams.supplier === "0"}
									onClick={() =>
										setSearchParams({
											...searchParams,
											supplier: "0",
										})
									}
								>
									Direct booking
								</TabItem>
							) : null}
							<TabItem
								active={searchParams.supplier === "all"}
								onClick={() =>
									setSearchParams({
										...searchParams,
										supplier: "all",
									})
								}
							>
								All
							</TabItem>
							{propParams?.booking_suppliers?.map((supplier) => {
								return (
									<TabItem
										key={supplier.id}
										active={searchParams.supplier === supplier.id.toString()}
										onClick={() =>
											setSearchParams({
												...searchParams,
												supplier: supplier.id.toString(),
											})
										}
									>
										{supplier.name}
									</TabItem>
								)
							})}
						</TabsList>
						<TabContent>
							<ListView<IBooking & { trip: ITrip }, TFilters>
								pageKey={`hotel-bookings/${queryToSearch(propParams)}`}
								fetch={(xhr, filters) =>
									XHR(xhr).getBookings({
										...filtersToRequestParams(filters),
										hotels: propParams?.hotels,
										hotel_groups: propParams?.hotel_groups,
										limit: 50,
									})
								}
								onPageChange={(page) => setParams({ ...params, page })}
								params={params}
							>
								{({ items }) => (
									<Table
										bordered
										hover
										responsive
										headers={["Trip Id" as React.ReactNode]
											.concat(
												showProperty ? ["Property" as React.ReactNode] : []
											)
											.concat([
												"Guest",
												"Checkin",
												"Rooms/Services",
												"Meal",
												"Stage",
												<Tooltip content="Booking Confirmation Number">
													BCN
												</Tooltip>,
												"Amount",
											])}
										alignCols={{ [showProperty ? 8 : 7]: "right" }}
										rows={items.map(
											({
												id,
												checkin,
												checkin_local,
												no_of_nights,
												no_of_children,
												no_of_adults,
												pax_string,
												trip,
												booked,
												booking_stage,
												currency,
												booked_price,
												booking_confirmation_id,
												booking_method,
												room_nights,
												payments_amount,
												cancellation_charges,
												paid_payments_amount,
												refunding_payments_amount,
												paid_refunding_payments_amount,
												extras,
												details,
												hotel,
											}) =>
												[trip.id as React.ReactNode]
													.concat(
														showProperty
															? [
																	(
																		<Box>
																			<Box>
																				<Link
																					to={generatePath(
																						"/hotels/:hotelId/bookings",
																						{
																							hotelId: hotel.id.toString(),
																						}
																					)}
																					color="accent"
																				>
																					<Box
																						title={hotel.name}
																						style={{ maxWidth: "150px" }}
																						textOverflow="truncate"
																						fontWeight="semibold"
																					>
																						{hotel.name}
																					</Box>
																				</Link>
																				<Box
																					display="flex"
																					alignItems="center"
																					fontSize="sm"
																					color="muted"
																				>
																					{joinAttributes(
																						<Box
																							display="inlineBlock"
																							style={{ maxWidth: "150px" }}
																							textOverflow="truncate"
																						>
																							{hotel?.address.location.name}
																						</Box>,
																						hotel?.stars ? (
																							<Stars stars={hotel?.stars} />
																						) : null
																					)}
																				</Box>
																			</Box>
																		</Box>
																	) as React.ReactNode,
																]
															: []
													)
													.concat([
														<Box
															display={showProperty ? "block" : "flex"}
															flexWrap="nowrap"
															alignItems="center"
															justifyContent="between"
														>
															<Box marginRight="2">
																<Link
																	to={generatePath("/trips/:tripId", {
																		tripId: trip.id.toString(),
																	})}
																	fontWeight="semibold"
																	color="accent"
																>
																	{trip.tourist?.name || (
																		<>
																			<Icons.AttentionSolid color="warning" />{" "}
																			N/A
																		</>
																	)}
																</Link>
															</Box>
															<Tooltip content={pax_string}>
																<Box
																	whiteSpace="preserve"
																	color="muted"
																	fontSize="sm"
																>
																	{no_of_adults}A
																	{no_of_children
																		? `, ${no_of_children}C`
																		: null}
																</Box>
															</Tooltip>
														</Box>,
														<Box>
															<Time
																timestamp={checkin}
																localTimestamp={checkin_local}
																format="D MMM"
															/>{" "}
															- {no_of_nights}N
														</Box>,
														<Box>
															{(room_nights || []).map(function (
																{ room_type, no_of_rooms, nights },
																index
															) {
																return (
																	<Tooltip
																		key={index}
																		content={
																			nights
																				.map((n) => withOrdinalSuffix(n))
																				.join(", ") +
																			" " +
																			pluralize("Night", nights.length)
																		}
																	>
																		<Box key={room_type.id} display="inline">
																			{no_of_rooms} {room_type.name}
																		</Box>
																	</Tooltip>
																)
															})}
															{extras?.length ? (
																<Box fontSize="sm">
																	{extras.map((extra) => (
																		<Box key={extra.id} maxWidth="xs">
																			{extra.service.name}
																		</Box>
																	))}
																</Box>
															) : null}
														</Box>,
														<Box>
															{details
																?.reduce<
																	Array<string>
																>((plans, d) => (plans.indexOf(d.meal_plan.name) === -1 ? plans.concat([d.meal_plan.name]) : plans), [])
																.join(", ")}
														</Box>,
														<Box>
															{getDisplayNameForStage(booking_stage.state)}
															{booked && booking_method ? (
																<Box
																	display="inline"
																	marginLeft="1"
																	fontSize="xs"
																	color="muted"
																	fontWeight="semibold"
																>
																	{booking_method}
																</Box>
															) : null}
														</Box>,
														booking_confirmation_id,
														<Box>
															{isNumeric(booked_price) ? (
																<Inline
																	fontWeight="semibold"
																	gap="1"
																	justifyContent="end"
																>
																	{refunding_payments_amount === null ? (
																		[
																			"changed",
																			"booked",
																			"dropped",
																			"trip_dropped",
																		].includes(booking_stage?.state) &&
																		paid_payments_amount === payments_amount ? (
																			<Box display="inlineBlock" marginLeft="1">
																				<Icons.OkCircleSolid color="success" />
																			</Box>
																		) : null
																	) : paid_refunding_payments_amount ===
																	  refunding_payments_amount ? (
																		<Box display="inlineBlock" marginLeft="1">
																			<Icons.OkCircleSolid color="success" />
																		</Box>
																	) : null}
																	<Text>Booking:</Text>
																	<Link
																		to={
																			generatePath(
																				"/trips/:tripId/accounting/payments",
																				{
																					tripId: trip.id.toString(),
																				}
																			) + `#hb_${id}`
																		}
																	>
																		<Money
																			amount={booked_price}
																			currency={currency}
																			showCurrency
																		/>
																	</Link>
																</Inline>
															) : null}
															{isNumeric(payments_amount) ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																>
																	{isNumeric(refunding_payments_amount) &&
																	isNumeric(cancellation_charges) ? (
																		<Box>
																			Net Payable:{" "}
																			<Money
																				amount={cancellation_charges}
																				currency={currency}
																				showCurrency
																			/>
																		</Box>
																	) : isNumeric(booked_price) &&
																	  Number(payments_amount || 0) ===
																			Number(booked_price || 0) ? null : (
																		<Box>
																			Net Payable:{" "}
																			<Money
																				amount={payments_amount}
																				currency={currency}
																				showCurrency
																			/>
																		</Box>
																	)}
																</Box>
															) : //show net payaable as 0 if booking_status is either changed, dropped or trip dropped
															booking_stage.state === "changed" ||
															  booking_stage.state === "dropped" ||
															  booking_stage.state === "trip_dropped" ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																>
																	Net Payable:{" "}
																	<Money
																		amount={0}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
															{isNumeric(paid_payments_amount) ? (
																<>
																	<Box
																		fontWeight="semibold"
																		fontSize="sm"
																		color="success"
																	>
																		Amount Paid:{" "}
																		<Money
																			amount={paid_payments_amount}
																			currency={currency}
																			showCurrency
																		/>
																	</Box>
																	{isNumeric(payments_amount) ? (
																		Number(payments_amount || 0) >
																		Number(paid_payments_amount || 0) ? (
																			<Box
																				fontWeight="semibold"
																				fontSize="sm"
																				color="muted"
																			>
																				Due Payable:{" "}
																				<Money
																					amount={
																						payments_amount -
																						paid_payments_amount
																					}
																					currency={currency}
																					showCurrency
																				/>
																			</Box>
																		) : null
																	) : null}
																</>
															) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
															isNumeric(payments_amount) &&
															  Number(payments_amount || 0) > 0 ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																>
																	Amount Paid:{" "}
																	<Money
																		amount={0}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
															{isNumeric(refunding_payments_amount) ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="warning"
																	title="Refund"
																>
																	Refund:{" "}
																	<Money
																		money={moneyParseByDecimal(
																			Number(
																				paid_refunding_payments_amount || 0
																			),
																			currency
																		)}
																		showCurrency
																	/>
																	{" / "}
																	<Money
																		amount={refunding_payments_amount}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
														</Box>,
													])
										)}
									/>
								)}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Box>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<Stack gap="1">
				<label htmlFor="stay_after">Stay During</label>
				<Box display="flex" alignItems="center" marginTop="1">
					<DatePickerField name="stay_after" />
					<Box>
						<Icons.SwitchHorizontal />
					</Box>
					<DatePickerField name="stay_before" rightAlign />
				</Box>
			</Stack>
			<SwitchInputField label="Has Due Payments" name="has_due_payments" />
			<SelectField
				select={SelectHotelBookingStages}
				name="booking_stages"
				label="Booking Stages"
				multiple
			/>
		</Stack>
	)
}

export function HotelPayments({
	hotel,
	paymentType,
}: {
	hotel: IHotel
	paymentType: "debit" | "credit"
}) {
	const { id } = hotel
	const hotels = useMemo(() => {
		return [id]
	}, [id])
	return (
		<Box paddingTop="8">
			<PaymentsList
				debit={paymentType === "debit" ? true : undefined}
				credit={paymentType === "credit" ? true : undefined}
				paymentContext="hotel"
				hotels={hotels}
				hideContact
			/>
		</Box>
	)
}

export function HotelAccountings({ hotel }: { hotel: IHotel }) {
	return (
		<Container paddingY="8">
			{hotel.accounting_accounts?.length ? (
				<Box>
					<Box textAlign="right" marginBottom="2">
						<ButtonLink
							to={generatePath("/hotels/:hotelId/payments/outgoing", {
								hotelId: String(hotel.id),
							})}
							size="sm"
						>
							View Due Payments <Icons.ChevronDown rotate="270" />
						</ButtonLink>
					</Box>
					<AccountsList accounts={hotel.accounting_accounts} />
				</Box>
			) : (
				<Stack gap="2">
					<Text fontSize="md" fontWeight="semibold">
						No accounting activities for this hotel
					</Text>
					<Text fontSize="sm" color="muted">
						Log payments regarding bookings or services for this hotel to manage
						the accounting.
					</Text>
					<Box>
						<ButtonLink to="../payments" size="sm">
							View Due Payments <Icons.ChevronDown rotate="270" />
						</ButtonLink>
					</Box>
				</Stack>
			)}
		</Container>
	)
}

export function HotelNotes({
	hotel,
	onChange,
}: {
	hotel: IHotel
	onChange: () => void
}) {
	const { id, room_types, meal_plans, notes } = hotel
	const { hasPermission } = useCheckPermissions()
	return (
		<Box>
			{notes?.length ? (
				<Box backgroundColor={"default"}>
					<Inline
						justifyContent={"between"}
						borderBottomWidth={"1"}
						borderColor={"muted"}
						paddingY={"4"}
						paddingX={"8"}
					>
						<Heading>Notes</Heading>
						<Box>
							<Component initialState={false}>
								{({ state, setState }) => (
									<>
										{hasPermission(PERMISSIONS.CREATE_HOTELS) ? (
											<Button onClick={() => setState(true)} size="sm">
												<Icons.Plus />
												Add new notes
											</Button>
										) : null}
										<Dialog open={state} onClose={() => setState(false)} xl>
											<Dialog.Header>
												<Dialog.Title>Add Hotel Notes</Dialog.Title>
											</Dialog.Header>
											<Dialog.Body>
												<CreateHotelNotes
													roomTypes={room_types}
													mealPlans={meal_plans}
													hotelId={id}
													onSuccess={() => {
														onChange()
														setState(false)
													}}
													isGlobalNotes={false}
												/>
											</Dialog.Body>
										</Dialog>
									</>
								)}
							</Component>
						</Box>
					</Inline>
				</Box>
			) : null}
			<HotelNotesList
				notes={notes}
				roomTypes={room_types}
				mealPlans={meal_plans}
				hotelId={id}
				onChange={onChange}
			/>
		</Box>
	)
}

interface IClosingIntervals {
	room_types: IHotel["room_types"]
	same_black_out_date_intervals_for_all_room_types: boolean
	same_sold_out_date_intervals_for_all_room_types: boolean
	blackout_date_intervals?: TDateInterval[]
	stop_sale_date_intervals?: TDateInterval[]
	stop_sale_date_for_room_types?: TSoldoutRoomInterval[]
	blackout_date_for_room_types?: TBlackoutRoomInterval[]
}
export function EditClosingIntervals({
	children,
	room_types,
	same_black_out_date_intervals_for_all_room_types,
	same_sold_out_date_intervals_for_all_room_types,
	onSubmit,
}: {
	children: (props: { onEdit: () => void }) => React.ReactNode
	room_types: IHotel["room_types"]
	same_black_out_date_intervals_for_all_room_types: boolean
	same_sold_out_date_intervals_for_all_room_types: boolean
	onSubmit: (data: {
		room_types: {
			id: number
			date_intervals: (string | number)[][]
		}[]
	}) => void
}) {
	const [isOpen, open, close] = useDialog()
	const {
		blackout_date_intervals,
		stop_sale_date_for_room_types,
		blackout_date_for_room_types,
		stop_sale_date_intervals,
	} = useMemo(
		() =>
			formatClosedDates(
				room_types,
				same_black_out_date_intervals_for_all_room_types,
				same_sold_out_date_intervals_for_all_room_types
			),
		[
			room_types,
			same_black_out_date_intervals_for_all_room_types,
			same_sold_out_date_intervals_for_all_room_types,
		]
	)
	const initialValue: IClosingIntervals = {
		room_types,
		same_black_out_date_intervals_for_all_room_types:
			same_black_out_date_intervals_for_all_room_types ?? true,
		same_sold_out_date_intervals_for_all_room_types:
			same_sold_out_date_intervals_for_all_room_types ?? true,
		stop_sale_date_for_room_types: stop_sale_date_for_room_types || [],
		blackout_date_for_room_types: blackout_date_for_room_types || [],
		blackout_date_intervals: blackout_date_intervals || [],
		stop_sale_date_intervals: stop_sale_date_intervals || [],
	}

	const validationSchema = Validator.object().shape({
		room_types: Validator.array(),

		same_black_out_date_intervals_for_all_room_types: Validator.boolean(),
		same_sold_out_date_intervals_for_all_room_types: Validator.boolean(),

		// -------- simple interval fields (used when "same for all" is TRUE) --------
		blackout_date_intervals: Validator.array().of(
			Validator.object().shape({
				start_date: Validator.date()
					.nullable()
					.when("end_date", {
						is: (end_date: Date | undefined) => !end_date,
						then: (schema) =>
							schema.required("Please select a start or end date"),
						otherwise: (schema) => schema.notRequired(),
					}),
				end_date: Validator.date().nullable(),
			})
		),
		stop_sale_date_intervals: Validator.array().of(
			Validator.object().shape({
				start_date: Validator.date()
					.nullable()
					.when("end_date", {
						is: (end_date: Date | undefined) => !end_date,
						then: (schema) =>
							schema.required("Please select a start or end date"),
						otherwise: (schema) => schema.notRequired(),
					}),
				end_date: Validator.date().nullable(),
			})
		),
		// -------- per-room-type cards (used when "same for all" is FALSE) --------
		stop_sale_date_for_room_types: Validator.array().of(
			Validator.object().shape({
				room_types: Validator.array()
					.min(1, "Please select atleast one room type")
					.required("Please select a room type"),
				black_out_date_intervals: Validator.array()
					.of(
						Validator.object().shape({
							start_date: Validator.date()
								.nullable()
								.when("end_date", {
									is: (end_date: Date | undefined) => !end_date,
									then: (schema) =>
										schema.required("Please select a start or end date"),
									otherwise: (schema) => schema.notRequired(),
								}),
							end_date: Validator.date().nullable(),
						})
					)
					.min(1, "Please add atleast one date interval"),
			})
		),

		blackout_date_for_room_types: Validator.array().of(
			Validator.object().shape({
				room_types: Validator.array()
					.min(1, "Please select atleast one room type")
					.required("Please select a room type"),
				black_out_date_intervals: Validator.array()
					.of(
						Validator.object().shape({
							start_date: Validator.date()
								.nullable()
								.when("end_date", {
									is: (end_date: Date | undefined) => !end_date,
									then: (schema) =>
										schema.required("Please select a start or end date"),
									otherwise: (schema) => schema.notRequired(),
								}),
							end_date: Validator.date().nullable(),
						})
					)
					.min(1, "Please add atleast one date interval"),
			})
		),
	})
	const validate = validateFormValues(validationSchema)
	return (
		<>
			{children({ onEdit: open })}
			<Dialog xl open={isOpen} onClose={close} title="Edit Closing Intervals">
				<Dialog.Body>
					<Form<IClosingIntervals>
						initialValues={initialValue}
						validate={validate}
						onSubmit={async (values) => {
							const {
								blackout_date_intervals,
								stop_sale_date_intervals,
								stop_sale_date_for_room_types,
								blackout_date_for_room_types,
								same_black_out_date_intervals_for_all_room_types,
								same_sold_out_date_intervals_for_all_room_types,
							} = values

							const data = {
								room_types: room_types.map((room) => {
									const date_intervals: [string, string, number][] = []

									// =====================================================
									// SOLD OUT / STOP SALE
									// =====================================================

									if (same_sold_out_date_intervals_for_all_room_types) {
										// Same sold-out dates for ALL room types
										if (stop_sale_date_intervals?.length) {
											stop_sale_date_intervals
												?.filter(
													(interval) =>
														interval?.start_date || interval?.end_date
												)
												.forEach((interval) => {
													const start = interval.start_date ?? interval.end_date

													const end = interval.end_date ?? interval.start_date

													if (!start || !end) return

													date_intervals.push([
														formatDate(start, "YYYY-MM-DD"),
														formatDate(end, "YYYY-MM-DD"),
														1, // stop sale
													])
												})
										}
									} else {
										// Different sold-out dates for different room types
										if (stop_sale_date_for_room_types?.length) {
											stop_sale_date_for_room_types
												?.filter((item) =>
													item.room_types?.some(
														(rt) => Number(rt.value) === room.id
													)
												)
												.forEach((item) => {
													item.black_out_date_intervals
														?.filter(
															(interval) =>
																interval?.start_date || interval?.end_date
														)
														.forEach((interval) => {
															const start =
																interval.start_date ?? interval.end_date

															const end =
																interval.end_date ?? interval.start_date

															if (!start || !end) return

															date_intervals.push([
																formatDate(start, "YYYY-MM-DD"),
																formatDate(end, "YYYY-MM-DD"),
																1, // stop sale
															])
														})
												})
										}
									}

									// =====================================================
									// BLACKOUT
									// =====================================================

									if (same_black_out_date_intervals_for_all_room_types) {
										// Same blackout dates for ALL room types
										if (blackout_date_intervals?.length) {
											blackout_date_intervals
												.filter(
													(interval) =>
														interval?.start_date || interval?.end_date
												)
												.forEach((interval) => {
													const start = interval.start_date ?? interval.end_date

													const end = interval.end_date ?? interval.start_date

													if (!start || !end) return

													date_intervals.push([
														formatDate(start, "YYYY-MM-DD"),
														formatDate(end, "YYYY-MM-DD"),
														0, // blackout
													])
												})
										}
									} else {
										// Different blackout dates for different room types
										if (blackout_date_for_room_types?.length) {
											blackout_date_for_room_types
												?.filter((item) =>
													item.room_types?.some(
														(rt) => Number(rt.value) === room.id
													)
												)
												.forEach((item) => {
													item.black_out_date_intervals
														?.filter(
															(interval) =>
																interval?.start_date || interval?.end_date
														)
														.forEach((interval) => {
															const start =
																interval.start_date ?? interval.end_date

															const end =
																interval.end_date ?? interval.start_date

															if (!start || !end) return

															date_intervals.push([
																formatDate(start, "YYYY-MM-DD"),
																formatDate(end, "YYYY-MM-DD"),
																0, // blackout
															])
														})
												})
										}
									}

									return {
										id: room.id,
										date_intervals,
									}
								}),
							}
							await onSubmit(data)
							close()
						}}
						mutators={{ ...arrayMutators }}
						subscription={{ submitting: true }}
					>
						{({ handleSubmit, submitting }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Stack gap="4">
									<HotelBlackoutDateIntervalsField roomTypes={room_types} />
									<Divider sm />
									<Inline gap="4" justifyContent="center">
										<Button type="submit" disabled={submitting}>
											{submitting ? "Updating..." : "Update"}
										</Button>
										<Button
											onClick={close}
											level="tertiary"
											disabled={submitting}
										>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

type BlackoutDateInterval = {
	name: string
}

type RoomType = {
	id: number
	name: string
	black_out_date_intervals: BlackoutDateInterval[]
}

type RoomTypeDateIntervalsProps = {
	roomTypes: RoomType[]
	title: string
	emptyMessage: string
	tooltipPrefix: string
}

function RoomTypeDateIntervalRestrictions({
	roomTypes,
	title,
	emptyMessage,
	tooltipPrefix,
}: RoomTypeDateIntervalsProps) {
	return (
		<>
			<Box fontSize="sm" fontWeight="semibold" color="muted">
				{title}
			</Box>

			<Box paddingBottom={"2"}>
				{roomTypes.length > 0 ? (
					joinAttributes(
						...roomTypes.map((roomType) => (
							<Box as="span" key={roomType.id}>
								{roomType.name}

								<Box as="span" fontSize="xs">
									{" ("}

									{joinAttributes(
										...roomType.black_out_date_intervals.map(
											(interval, index) => (
												<Box
													as="span"
													key={index}
													fontSize="xs"
													color="danger"
													title={`${tooltipPrefix} ${interval.name}`}
												>
													{interval.name}
												</Box>
											)
										)
									)}

									{" )"}
								</Box>
							</Box>
						))
					)
				) : (
					<Text fontSize="sm">{emptyMessage}</Text>
				)}
			</Box>
		</>
	)
}
